import type { Account, Txn } from "./types";

/**
 * Linking transactions to accounts.
 *
 * A transaction carries only a free-text `account` label copied verbatim from
 * Empower, which reports the SAME account inconsistently — bare ("Investor
 * Checking") one pull, masked ("Investor Checking ...899") the next — and
 * sometimes under a label that matches no current account at all (closed
 * Mint-era accounts like "Chase Freedom"). This module resolves a label to a
 * stable Account.id so the ledger displays + filters one canonical account
 * instead of Empower's noise.
 *
 * Resolution order (see buildAccountResolver): an Account.alias exact-match
 * (case-insensitive) wins — the mask lives IN the alias, so same-named accounts
 * (Jeff's vs Mavis's "Roth IRA") stay distinct — then an exact, UNIQUE account
 * name. Ambiguous or unknown labels resolve to null and keep their raw label.
 */

/** Normalize a label for matching: lowercase, and collapse every whitespace run
 * — including the non-breaking spaces Empower sprinkles into labels (e.g.
 * "Visa® Card") — to a single ASCII space. */
const normLabel = (s: string): string => (s || "").toLowerCase().replace(/\s+/g, " ").trim();

/** Build a fast label→accountId resolver from the account roster. */
export function buildAccountResolver(accounts: Account[]): (label: string) => string | null {
  const byAlias = new Map<string, string>();
  const byName = new Map<string, string | null>(); // null marks an ambiguous (non-unique) name
  for (const a of accounts) {
    for (const al of a.aliases ?? []) {
      const k = normLabel(al);
      if (k && !byAlias.has(k)) byAlias.set(k, a.id);
    }
    const n = normLabel(a.name);
    byName.set(n, byName.has(n) ? null : a.id);
  }
  return (label) => {
    const l = normLabel(label);
    if (!l) return null;
    return byAlias.get(l) ?? byName.get(l) ?? null;
  };
}

/** One-shot convenience; prefer buildAccountResolver when resolving in bulk. */
export function resolveAccountId(label: string, accounts: Account[]): string | null {
  return buildAccountResolver(accounts)(label);
}

/** Owner-disambiguated display label for an account id (e.g. "Roth IRA (Jeff)"
 * when two accounts share a name). Null when the id isn't in the roster. */
export function accountLabelById(id: string, accounts: Account[]): string | null {
  const a = accounts.find((x) => x.id === id);
  if (!a) return null;
  const collides = accounts.some((x) => x.id !== a.id && x.name === a.name);
  return collides ? `${a.name} (${a.owner})` : a.name;
}

/** What to show for a transaction's account: the linked entity's label
 * (owner-disambiguated). Falls back to the bare id only if the account entity is
 * somehow missing from the roster. */
export function txnAccountLabel(t: Pick<Txn, "accountId">, accounts: Account[]): string {
  return accountLabelById(t.accountId, accounts) ?? t.accountId;
}

/** Stable grouping/filter key for a transaction's account — its account id. */
export function txnAccountKey(t: Pick<Txn, "accountId">): string {
  return t.accountId;
}
